import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'clsx';
import { Button, useTransitionEnd } from '@kcd/design-system';

import { useSnackbarContext } from './context';
import type { SnackbarProps } from './types';

import styles from './Snackbar.module.css';

const defualtButtonProps: SnackbarProps['buttonProps'] = {
  size: 'xs',
  variant: 'primary',
  children: '확인',
};

const Snackbar = ({
  id,
  decoration,
  text,
  closeInMilliseconds,
  className,
  buttonProps,
  offset,
  onClick,
}: SnackbarProps) => {
  const { onDelete } = useSnackbarContext();
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const btmProps: SnackbarProps['buttonProps'] = {
    ...defualtButtonProps,
    ...buttonProps,
  };

  const handleClose = useCallback(() => {
    const el = ref.current;
    if (el) {
      el.style.opacity = '0';
    }
  }, []);

  const onClickButton = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (onClick) {
        onClick(e);
        handleClose();
        if (timer.current) {
          clearTimeout(timer.current);
        }
      }
    },
    [handleClose, onClick],
  );

  useTransitionEnd({
    ref,
    propertyName: 'opacity',
    onComplete: onDelete,
  });

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      handleClose();
    }, closeInMilliseconds);

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [closeInMilliseconds, handleClose]);

  const bottomPosition = useMemo(() => {
    if (offset && typeof offset === 'number') {
      return `${offset}px`;
    }

    return offset ? '122px' : '16px';
  }, [offset]);

  return (
    <div
      key={id}
      ref={ref}
      className={cx(styles.snackbar, className)}
      style={{ bottom: bottomPosition, position: 'fixed' }}
    >
      <div className={styles.leftWrap}>
        <p className={cx(styles.decoration, styles.textEllipsis)}>
          {decoration}
        </p>
        <p className={cx(styles.text, styles.textEllipsis)}>{text}</p>
      </div>
      <Button
        {...buttonProps}
        className={styles.snackbarButton}
        onClick={onClickButton}
      >
        {btmProps.children}
      </Button>
    </div>
  );
};

export default Snackbar;
